import React, { useState } from "react";
import { NavLink, Link, Outlet, useLocation } from "react-router-dom";
import { Wordmark, MetaLabel } from "../brand/Brand";
import { useAuth } from "../auth/AuthContext";
import "./styles.css";

// Compact inline icon set for the mobile bottom bar / menu sheet. Stroke-based,
// inherits currentColor so active/inactive colour comes from CSS.
const ICONS = {
  overview: "M3 12l9-9 9 9M5 10v10h14V10",
  requests: "M4 6h16M4 12h16M4 18h10",
  projects: "M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
  leads: "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21v-1a6 6 0 0 1 6-6h2a6 6 0 0 1 6 6v1M19 8v6M22 11h-6",
  case: "M4 5h16v14H4zM4 15l4-4 3 3 4-5 5 6",
  layout: "M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM3 9h18M9 21V9",
  products: "M21 8l-9-5-9 5 9 5 9-5zM3 8v8l9 5 9-5V8M12 13v8",
  usage: "M3 12h4l3 8 4-16 3 8h4",
  users: "M8 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM1 21v-1a6 6 0 0 1 6-6h2a6 6 0 0 1 6 6v1M17 3.5a4 4 0 0 1 0 7.5M23 21v-1a5 5 0 0 0-4-4.9",
  accounts: "M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01",
  blog: "M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z",
  messages: "M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
  notifications: "M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0",
  invoices: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M9 13h6M9 17h6",
  home: "M3 12l9-9 9 9M5 10v10h14V10",
  more: "M5 12h.01M12 12h.01M19 12h.01",
};

const DashIcon = ({ name }) => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={ICONS[name] || ICONS.more} />
  </svg>
);

const MAX_TABS = 5; // bottom bar shows at most this many slots

const DashboardShell = ({ navItems, roleLabel }) => {
  const { user, profile, logout } = useAuth();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const current =
    navItems.find((n) => location.pathname.startsWith(n.to)) || navItems[0];

  const isAdmin = !!profile?.isAdmin;
  const isClient = !!profile?.isClient;
  const dualRole = isAdmin && isClient;
  const inAdmin = location.pathname.startsWith("/admin");
  const switchTo = inAdmin ? "/dashboard" : "/admin";
  const switchLabel = inAdmin ? "View as client" : "Back to admin";

  // If every item fits, the bottom bar shows them all. Otherwise it shows the
  // items flagged `primary` (or the first few) plus a "More" button that opens
  // the full list in a sheet — so nothing is ever unreachable.
  const fits = navItems.length <= MAX_TABS;
  const primaryItems = navItems.filter((n) => n.primary);
  let barItems;
  if (fits) barItems = navItems;
  else if (primaryItems.length >= 1 && primaryItems.length <= MAX_TABS - 1)
    barItems = primaryItems;
  else barItems = navItems.slice(0, MAX_TABS - 1);

  const closeMore = () => setMoreOpen(false);

  return (
    <div className="fw-dash">
      <aside className="fw-dash__rail">
        <Link to="/" className="fw-dash__brand">
          <Wordmark size={18} />
        </Link>

        <div className="fw-dash__user">
          <MetaLabel size={10}>{roleLabel}</MetaLabel>
          <span className="fw-dash__user-name">
            {profile?.displayName || user?.email}
          </span>
        </div>

        <nav className="fw-dash__nav">
          <div className="fw-dash__nav-label">Workspace</div>
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) => (isActive ? "is-active" : "")}
            >
              <span>{n.label}</span>
              {n.badge != null && <span className="fw-chip">{n.badge}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="fw-dash__rail-foot">
          <button className="fw-dash__signout" onClick={logout}>
            Sign out →
          </button>
        </div>
      </aside>

      <main className="fw-dash__main">
        {/* Mobile-only brand strip (desktop uses the sidebar) */}
        <div className="fw-dash__mobilebrand">
          <Link to="/" className="fw-dash__brand">
            <Wordmark size={15} />
          </Link>
          {dualRole && (
            <Link to={switchTo} className="fw-dash__mobilebrand-switch">
              {inAdmin ? "Client view →" : "Admin →"}
            </Link>
          )}
        </div>

        <header className="fw-dash__topbar">
          <div className="fw-dash__topbar-title">
            <MetaLabel size={10}>
              {roleLabel} · {current?.label}
            </MetaLabel>
            <h1>{current?.heading || current?.label}</h1>
          </div>
          {dualRole && (
            <Link to={switchTo} className="fw-dash__role-switch">
              <span className="fw-dash__role-switch-meta">
                Currently · {inAdmin ? "Admin" : "Client"}
              </span>
              <span className="fw-dash__role-switch-label">{switchLabel} →</span>
            </Link>
          )}
        </header>
        <div className="fw-dash__content">
          <Outlet />
        </div>
      </main>

      {/* Mobile-only bottom tab bar */}
      <nav className="fw-dash__tabbar" aria-label="Sections">
        {barItems.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            className={({ isActive }) =>
              "fw-dash__tab" + (isActive ? " is-active" : "")
            }
          >
            <DashIcon name={n.icon} />
            <span>{n.label}</span>
            {n.badge != null && <span className="fw-dash__tab-badge">{n.badge}</span>}
          </NavLink>
        ))}
        {!fits && (
          <button
            type="button"
            className={"fw-dash__tab" + (moreOpen ? " is-active" : "")}
            onClick={() => setMoreOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
          >
            <DashIcon name="more" />
            <span>More</span>
          </button>
        )}
      </nav>

      {/* "More" sheet — the full section list */}
      {moreOpen && (
        <div
          className="fw-dash__sheet-backdrop"
          onClick={closeMore}
          role="dialog"
          aria-modal="true"
        >
          <div className="fw-dash__sheet" onClick={(e) => e.stopPropagation()}>
            <div className="fw-dash__sheet-head">
              <MetaLabel size={10}>{roleLabel} · Menu</MetaLabel>
              <button
                className="fw-dash__sheet-close"
                onClick={closeMore}
                aria-label="Close menu"
              >
                ✕
              </button>
            </div>
            <div className="fw-dash__sheet-grid">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={closeMore}
                  className={({ isActive }) =>
                    "fw-dash__sheet-item" + (isActive ? " is-active" : "")
                  }
                >
                  <DashIcon name={n.icon} />
                  <span>{n.label}</span>
                  {n.badge != null && <span className="fw-chip">{n.badge}</span>}
                </NavLink>
              ))}
            </div>
            <button
              className="fw-dash__signout fw-dash__sheet-signout"
              onClick={() => {
                closeMore();
                logout();
              }}
            >
              Sign out →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardShell;
